import { isDesktop, isLarge, isMobile, isTablet } from 'components/Shared/responsive';
import React from 'react';
import { useSelector } from 'react-redux';
import styles from './masonry.scss';

interface MasonryProps {
  columnAmount: number;
  children: React.ReactNode | React.ReactNode[];
}

interface Column {
  [key: number]: React.ReactNode[];
}

const getItems = ({ columnAmount, children }: MasonryProps): React.ReactNode[] => {
  if (children === null || children === undefined || !Array.isArray(children)) {
    return [];
  }

  const items = children.reduce((columns: Column, item, index) => {
    const columnIndex = index % columnAmount;

    const column = columns[columnIndex] ? [...columns[columnIndex], item] : [item];

    return {
      ...columns,
      [columnIndex]: column
    };
  }, {}) as Record<number, React.ReactNode>;

  return items ? Object.values(items) : [];
};

const MAX_PERCENTAGE = 100;
const getColumnWidth = ({ columnAmount }: Pick<MasonryProps, 'columnAmount'>) => {
  return `${MAX_PERCENTAGE / columnAmount}%`;
};

const Columns = ({ columnAmount, children }: MasonryProps) => {
  const items = getItems({ columnAmount, children });
  const columnWidth = getColumnWidth({ columnAmount });

  return (
    <>
      {items.map((items, index) => (
        <div style={{ width: columnWidth }} className={styles.column} key={index}>
          <>{items}</>
        </div>
      ))}
    </>
  );
};

const useColumnAmount = () => {
  const _isMobile = useSelector(isMobile);
  const _isTablet = useSelector(isTablet);
  const _isLarge = useSelector(isLarge);
  const _isDesktop = useSelector(isDesktop);

  const sizes = {
    mobile: 1,
    tablet: 2,
    large: 3,
    desktop: 4,
    default: 5
  };

  if (_isMobile) {
    return sizes.mobile;
  }

  if (_isTablet) {
    return sizes.tablet;
  }

  if (_isLarge) {
    return sizes.large;
  }

  if (_isDesktop) {
    return sizes.desktop;
  }

  return sizes.default;
};

export const Masonry = ({ children }: Pick<MasonryProps, 'children'>) => {
  const columnAmount = useColumnAmount();

  return (
    <div className={styles.masonry}>
      <Columns columnAmount={columnAmount}>{children}</Columns>
    </div>
  );
};
